/**
 * スキル単位の間隔反復メモリ。
 *
 * Progress（朱印・クリア集合）とは独立させる。理由:
 * - Progress は F-009 同期の grow-only set であり、スキーマ変更が Worker まで波及する
 * - 間隔反復の時刻は端末ローカルの「いつ打ったか」が本質で、当面は同期不要
 */

export const DAY_MS = 24 * 60 * 60 * 1000;

/** 成功回数に応じた次回復習までの間隔（expanding）。上限 30 日。 */
const INTERVALS_MS: readonly number[] = [
  1 * DAY_MS,
  3 * DAY_MS,
  7 * DAY_MS,
  14 * DAY_MS,
  30 * DAY_MS,
];

export interface SkillMemoryEntry {
  readonly lastPracticedAt: string;
  readonly successes: number;
}

export interface SkillMemory {
  readonly entries: ReadonlyMap<string, SkillMemoryEntry>;
}

export function createSkillMemory(): SkillMemory {
  return { entries: new Map() };
}

export function nextIntervalMs(successes: number): number {
  if (successes <= 0) {
    return INTERVALS_MS[0] as number;
  }
  const index = Math.min(successes - 1, INTERVALS_MS.length - 1);
  return INTERVALS_MS[index] as number;
}

export function isSkillDue(entry: SkillMemoryEntry, nowMs: number): boolean {
  const elapsed = nowMs - Date.parse(entry.lastPracticedAt);
  return elapsed >= nextIntervalMs(entry.successes);
}

/** 期限到来スキルを、最後に練習してから古い順で返す。 */
export function dueSkillKeys(memory: SkillMemory, nowMs: number = Date.now()): readonly string[] {
  return [...memory.entries.entries()]
    .filter(([, entry]) => isSkillDue(entry, nowMs))
    .sort((a, b) => Date.parse(a[1].lastPracticedAt) - Date.parse(b[1].lastPracticedAt))
    .map(([key]) => key);
}

export function withSkillPractice(
  memory: SkillMemory,
  skillKey: string,
  practicedAt: string = new Date().toISOString(),
): SkillMemory {
  const previous = memory.entries.get(skillKey);
  const next: SkillMemoryEntry = {
    lastPracticedAt: practicedAt,
    successes: (previous?.successes ?? 0) + 1,
  };
  const entries = new Map(memory.entries);
  entries.set(skillKey, next);
  return { entries };
}

/**
 * 2つのスキルメモリを合併する。同じスキルは
 * `lastPracticedAt` の新しい方・`successes` の大きい方を取る（可換・冪等）。
 */
export function mergedSkillMemory(a: SkillMemory, b: SkillMemory): SkillMemory {
  const entries = new Map(a.entries);
  for (const [key, right] of b.entries) {
    const left = entries.get(key);
    if (left === undefined) {
      entries.set(key, right);
      continue;
    }
    entries.set(key, {
      lastPracticedAt:
        left.lastPracticedAt >= right.lastPracticedAt
          ? left.lastPracticedAt
          : right.lastPracticedAt,
      successes: Math.max(left.successes, right.successes),
    });
  }
  return { entries };
}
